import { useState, useEffect } from "react";
import API, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Phone, ShieldCheck, ArrowRight } from "lucide-react";

const MYERBA_LOGO = "/api/files/branding/myerba.png";
const TAGNPAY_LOGO = "https://customer-assets.emergentagent.com/job_images-34/artifacts/tdji2o49_tagnpay-logo-new.png";

export default function LoginPage() {
  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [incomplete, setIncomplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // If the user lands here because their KYC is pending (router redirect),
  // proactively log them out server-side and surface the under-review banner.
  useEffect(() => {
    if (searchParams.get("pending") === "1") {
      setError("Your KYC is under review. You'll be able to log in once the admin approves your registration. The process may take 24–48 hours.");
      logout().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (mobile.length < 10) { setError("Enter a valid 10-digit mobile number"); return; }
    setLoading(true); setError(""); setIncomplete(false);
    try { await API.post("/auth/send-otp", { mobile }); setStep("otp"); }
    catch (err) {
      const detail = err.response?.data?.detail;
      // Point 43: incomplete signup — guide them to finish registration.
      if (detail && typeof detail === "object" && detail.code === "incomplete_registration") {
        setIncomplete(true);
        setError(detail.message || "Your registration is incomplete. Please complete your registration to continue.");
      } else {
        setError(formatError(detail));
      }
    }
    finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 6) { setError("Enter 6-digit OTP"); return; }
    setLoading(true); setError("");
    try {
      const { data } = await API.post("/auth/verify-otp", { mobile, otp });
      login(data.user, data.access_token);
      navigate(data.is_new_user ? "/app/profile" : "/app");
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel — clean teal brand panel, no background photo */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#004A52] via-[#008A99] to-[#004A52]">
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="w-fit">
            <img src={MYERBA_LOGO} alt="MyErba" className="h-16 w-auto object-contain" />
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[1.1]" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Loyalty that<br/>powers your lab</h1>
            <p className="text-sm text-white/70 mt-4 leading-relaxed max-w-sm">Scan reagent kits. Earn rewards. Access product information, activate warranties, and redeem points.</p>
            <div className="flex gap-8 mt-10">
              {[{ v: "100+", l: "Countries" }, { v: "150Cr+", l: "Tests" }, { v: "200M+", l: "Served" }].map(s => (
                <div key={s.l}><p className="text-2xl font-bold" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{s.v}</p><p className="text-[10px] text-white/55 uppercase tracking-wider font-bold">{s.l}</p></div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 opacity-50"><span className="text-[9px] uppercase tracking-[0.15em] font-semibold">Powered by</span><img src={TAGNPAY_LOGO} alt="TagNPay" className="h-4" /></div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#F4F7FA]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <img src={MYERBA_LOGO} alt="MyErba" className="inline-block h-20 w-auto object-contain" data-testid="login-myerba-logo" />
          </div>
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{step === "mobile" ? "Welcome" : "Verify OTP"}</h2>
            <p className="text-gray-500 mt-1.5 text-sm">{step === "mobile" ? "Enter your registered mobile number" : `Code sent to +91 ${mobile}`}</p>
          </div>
          {error && <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-medium" data-testid="login-error">{error}</div>}
          {incomplete && (
            <button type="button" onClick={() => navigate("/register")} className="mb-5 w-full h-11 rounded-xl bg-[#008A99] hover:bg-[#006670] text-white font-bold text-sm shadow-lg shadow-[#008A99]/20" data-testid="complete-registration-button">
              Complete Registration
            </button>
          )}
          {step === "mobile" ? (
            <form onSubmit={sendOtp} className="space-y-5">
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 block">Mobile Number</label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-14 bg-gray-100 rounded-l-xl flex items-center justify-center border-r border-gray-200"><span className="text-sm font-bold text-gray-500">+91</span></div>
                  <Input data-testid="mobile-input" type="tel" placeholder="Enter 10-digit number" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} className="pl-18 h-12 rounded-xl border-gray-200 text-base font-medium tracking-wide" style={{ paddingLeft: '4rem', height: '48px' }} maxLength={10} />
                </div>
              </div>
              <Button data-testid="send-otp-button" type="submit" disabled={loading || mobile.length < 10} className="w-full h-12 rounded-xl bg-[#008A99] hover:bg-[#006670] text-white font-bold text-sm shadow-lg shadow-[#008A99]/20 disabled:shadow-none">
                {loading ? "Sending..." : "Get OTP"}{!loading && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-5">
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 block">Verification Code</label>
                <Input data-testid="otp-input" type="text" placeholder="Enter 6-digit OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} className="h-12 rounded-xl border-gray-200 text-lg font-bold tracking-[0.4em] text-center" maxLength={6} />
              </div>
              <Button data-testid="verify-otp-button" type="submit" disabled={loading || otp.length < 6} className="w-full h-12 rounded-xl bg-[#008A99] hover:bg-[#006670] text-white font-bold text-sm shadow-lg shadow-[#008A99]/20 disabled:shadow-none">
                {loading ? "Verifying..." : "Verify & Login"}
              </Button>
              <button type="button" onClick={() => { setStep("mobile"); setOtp(""); setError(""); }} className="w-full text-center text-sm text-[#008A99] hover:underline font-medium" data-testid="change-mobile-button">Change mobile number</button>
            </form>
          )}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">New User? <a href="/register" className="text-[#008A99] font-semibold hover:underline" data-testid="register-link">Sign Up</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}
